import React, { useContext } from "react";
import { FormattedMessage } from "react-intl";

import { CartContext } from "../../contexts/CartContext";
import { ExtendedCartMerchandise } from "../../types/shopify-components";
import { paragraph1, heading3, paragraph3 } from "../../utils/styles";
import Money from "../Money";

import QuantityControl from "./QuantityControl";

export const TEST_ID_LINE_ITEM_COMPARE_AT_PRICE =
  "single-product-line-item-original-price";
export const TEST_ID_LINE_ITEM_PRICE = "single-product-line-item-price";
export const TEST_ID_LINE_ITEM_TITLE = "single-product-line-item-title";

export interface SingleProductLineItemProps {
  merchandise: ExtendedCartMerchandise;
  connectedMerchandises?: ExtendedCartMerchandise[];
  title: string;
}

const SingleProductLineItem: React.FC<SingleProductLineItemProps> = ({
  connectedMerchandises,
  merchandise,
  title,
}) => {
  const {
    availableForSale,
    id,
    price: priceRaw,
    compareAtPrice: compareAtPriceRaw,
  } = merchandise;
  const { localItems } = useContext(CartContext);

  const price = Number(priceRaw.amount);
  const compareAtPrice = compareAtPriceRaw
    ? Number(compareAtPriceRaw.amount)
    : null;
  const quantity =
    localItems.find(item => item.merchandise.id === id)?.input?.quantity || 0;

  return (
    <article
      className="flex justify-between"
      data-testid={`single-product-line-item-${id}`}
    >
      <div className="flex flex-column flex-row-l w-100 mr3 w-60">
        <div className="w-100 w-auto-l">
          <h2
            className={`${paragraph1} mt0 mb2 mb0-l mr3-l`}
            data-testid={TEST_ID_LINE_ITEM_TITLE}
          >
            {title}
          </h2>
        </div>
        <div className="w-100 w-auto-l flex-grow-1 tc-l">
          <div>
            {compareAtPrice && compareAtPrice > price && (
              <span
                className={`${heading3} nowrap w-25 ttu strike mr2 dark-gray`}
                data-testid={TEST_ID_LINE_ITEM_COMPARE_AT_PRICE}
              >
                <Money value={compareAtPrice} />
              </span>
            )}
            <span
              className={`${heading3} nowrap w-25 ttu`}
              data-testid={TEST_ID_LINE_ITEM_PRICE}
            >
              <FormattedMessage
                id="lineItem.pricePerUnit"
                values={{ value: <Money value={price} /> }}
              />
            </span>
          </div>
          {!availableForSale && (
            <div className={`${paragraph3} pink mt2`}>
              <FormattedMessage id="lineItem.temporarilyNotAvailable" />
            </div>
          )}
        </div>
      </div>
      <div className="w-20 mr3-l">
        <div className="nt2">
          <QuantityControl
            allowZero={true}
            connectedMerchandises={connectedMerchandises}
            quantity={quantity}
            merchandise={merchandise}
            updateOnChange={false}
          />
        </div>
      </div>
      <div className="w-20 tr dn db-l">
        <span className={`${heading3} w-20 tr`}>
          <Money value={quantity * price} />
        </span>
      </div>
    </article>
  );
};

export default SingleProductLineItem;
